import { Component, QueryList, ViewChildren, ViewChild, ElementRef } from "@angular/core";
import { marked } from "marked";
import { ProjectService } from "../../shared/services/project.service";
import { take } from "rxjs/operators";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzModalService } from "ng-zorro-antd/modal";
import { animate, style, transition, trigger } from "@angular/animations";
import { ImageFlipperService } from "../image-flipper.service";
import { forkJoin, Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppConfig } from "../../app.config";
import { ImageLoaderService } from "../../shared/services/image-loader.service";
import { CopilotContextService } from "../../shared/services/copilot-context.service";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

interface AgentStep {
  tool: string;
  input?: any;
}

interface ChatMsg {
  role: "user" | "xwi-gpt" | "slice-image" | "shot-image" | "gif-image" | "rag" | "chart" | "links";
  content: any;
  drive_images?: string[];
  images_loading?: boolean;
  error?: string;
  timestamp?: number;
  streaming?: boolean;
  steps?: AgentStep[];      // "how I got this" — tools the agent ran
  showSteps?: boolean;      // UI: steps panel expanded
}

const LIVE_CONVO_STORAGE_KEY = "xwai_live_conversation";

// custom renderer for additional settings like parsing link to open in new tab
const renderer = new marked.Renderer();
const originalLinkRenderer = renderer.link;
// Override the link renderer
renderer.link = (link: any) => {
  // get default HTML
  const html = originalLinkRenderer.call(renderer, link);
  // Use a regular expression to add target and rel attributes
  // This avoids adding them if the <a> tag already has them
  return html.replace(/^<a/, '<a target="_blank" rel="noopener noreferrer"');
};


@Component({
    selector: "app-copilot-bot",
    templateUrl: "./copilot-bot.component.html",
    styleUrls: ["./copilot-bot.component.less"],
    animations: [
        trigger("slideIn", [
            transition(":enter", [
                style({ opacity: 0 }),
                animate("300ms ease-out", style({ opacity: 1 })),
            ]),
        ]),
        trigger("fadeInOut", [
            transition(":enter", [
                style({ opacity: 0 }),
                animate("300ms ease-out", style({ opacity: 1 })),
            ]),
            transition(":leave", [animate("300ms ease-in", style({ opacity: 0 }))]),
        ]),
    ],
    standalone: false
})
export class CopilotBotComponent {
  @ViewChildren("allTheseThings") things: QueryList<any>;
  @ViewChild("messageInput") messageInput: ElementRef<HTMLTextAreaElement>;
  @ViewChild("chatScroll") chatScroll: ElementRef<HTMLDivElement>;

  thread_id: string;
  messages: ChatMsg[] = [];

  question: string = "";
  answer_loading: boolean = false;
  thread_loading: boolean = true;
  // When false, answers are fetched via a single non-streaming POST instead of
  // SSE. Use this when a proxy/load-balancer in front of the backend buffers
  // `text/event-stream` (the answer never arrives, with no error) — the plain
  // POST returns one JSON payload and surfaces backend errors as a 500.
  useStreaming: boolean = true;
  // Live tool-progress note shown while streaming (e.g. "Querying SQL Database…")
  streaming_status: string = "";
  is_viewing_saved_conversation: boolean = false;
  current_saved_key: string | null = null;
  saved_convos: { [key: string]: ChatMsg[] } = {};
  show_scroll_fab: boolean = false;
  copied_index: number | null = null;
  // Set when the user maximises a table; rendered in the full-width overlay.
  expandedTableHtml: SafeHtml | null = null;
  expandedTableCsv: string = "";

  starter_prompts: string[] = [
    "Can you list all the Seam2D projects?",
    "Show me the latest Vp model for one of my jobs",
    "What frequency and functional do my recent jobs use?",
    "Compare the predicted and field shot data for a job",
  ];

  //cache for current active conversation - persists in memory when viewing saved conversations
  // doesn't persist across page reloads tho
  current_conversation_cache: ChatMsg[] = [];

  constructor(
    private projectService: ProjectService,
    private notificationService: NzNotificationService,
    private modalService: NzModalService,
    private imageFlipperService: ImageFlipperService,
    private imageLoader: ImageLoaderService,
    public copilotContext: CopilotContextService,
    private sanitizer: DomSanitizer,
  ) {}

  /** Page-aware quick prompts, surfaced when a job/project is in context. */
  get quickPrompts() {
    return this.copilotContext.quickPrompts();
  }

  get hasPageContext(): boolean {
    return !!this.copilotContext.context();
  }

  get contextLabel(): string {
    const ctx = this.copilotContext.context();
    if (!ctx) return "";
    return ctx.jobName || ctx.projectName || "";
  }

  useQuickPrompt(prompt: string) {
    if (this.isInputDisabled) return;
    this.messages.push({ role: "user", content: prompt, timestamp: Date.now() });
    this.updateCurrentConversationCache();
    this.persistLiveConversation();
    this.requestAnswer(prompt);
  }

  private destroy$ = new Subject<void>();
  async ngOnInit() {
    try {
      this.thread_loading = true;
      const res = await this.projectService.get_xwi_gpt_thread_id().toPromise();
      this.thread_id = res["data"];
      this.thread_loading = false;
    } catch (err) {
      console.error("Failed to load thread ID:", err);
      this.thread_loading = false;
      this.notificationService.error(
        "Error",
        "Failed to initialize chat. Please refresh the page."
      );
    }

    // restore the live conversation from a previous visit (display only;
    // the agent thread itself is freshly initialized above)
    this.restoreLiveConversation();

    this.projectService
      .getSavedConversations()
      .pipe(take(1))
      .subscribe({
        next: (conversations) => {
          // convert backend format to messages format
          for (const key in conversations) {
            if (conversations.hasOwnProperty(key)) {
              this.saved_convos[key] = conversations[key].messages;
            }
          }
        },
        error: (err) => {
          console.error("Failed to load conversations:", err);
        },
      });

    this.imageFlipperService.new_chat$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.startNewChat(false);
      });

    this.imageFlipperService.saved_convo_key$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (key) => {
          this.current_saved_key = key;
          if (key) {
            // save current conversation before switching to saved one
            if (!this.is_viewing_saved_conversation) {
              this.current_conversation_cache = [...this.messages];
            }

            this.is_viewing_saved_conversation = true;
            // Check if we have it cached first
            if (this.saved_convos[key]) {
              this.messages = [...this.saved_convos[key]]; // create copy to avoid reference issues
            } else {
              // Load from backend if not cached
              this.projectService
                .getSavedConversationByKey(key)
                .pipe(take(1))
                .subscribe({
                  next: (conversation) => {
                    if (conversation && conversation.messages) {
                      this.messages = [...conversation.messages];
                      this.saved_convos[key] = conversation.messages;
                      this.processMessagesForImages(this.messages);
                    } else {
                      console.warn("Conversation not found:", key);
                      this.restoreOrResetMessages();
                      this.is_viewing_saved_conversation = false;
                    }
                  },
                  error: (err) => {
                    console.error("Failed to load conversation:", key, err);
                    this.restoreOrResetMessages();
                    this.is_viewing_saved_conversation = false;
                  },
                });
            }
          } else {
            // restore current conversation when switching back
            this.is_viewing_saved_conversation = false;
            this.restoreOrResetMessages();
          }
        },
        error: (err) => {
          console.error("Error in saved_convo_key$ subscription:", err);
          this.is_viewing_saved_conversation = false;
          this.restoreOrResetMessages();
        },
      });
  }

  /**
   * Iterates over messages and triggers image loading for GDrive and GIFs.
   */
  private processMessagesForImages(messages: ChatMsg[]) {
    messages.forEach(msg => {
      if (msg.role === 'xwi-gpt') {
        // Only load if not already loaded
        if (msg.drive_images && msg.drive_images.some(img => !img)) {
          this.parseAndLoadGoogleDriveImages(msg, true); // Force re-load
        } else if (!msg.drive_images && typeof msg.content === 'string') {
          this.parseAndLoadGoogleDriveImages(msg);
        }
      } else if (msg.role === 'gif-image') {
        // Only load if not already loaded
        if (msg.content && !msg.content.local_image_urls) {
          this.fetchAndDisplayImages(msg);
        }
      }
    });
  }

  private resetMessages() {
    this.messages = [];
  }

  private restoreOrResetMessages() {
    if (this.current_conversation_cache.length > 0) {
      this.messages = [...this.current_conversation_cache];
    } else {
      // no cached conversation, use the empty state
      this.resetMessages();
    }
  }

  get isInputDisabled(): boolean {
    return (
      this.thread_loading ||
      this.answer_loading ||
      this.is_viewing_saved_conversation
    );
  }

  get inputPlaceholder(): string {
    if (this.thread_loading) {
      return "Initializing chat...";
    }
    if (this.is_viewing_saved_conversation) {
      return "Viewing a saved conversation — go back to the live chat to keep talking";
    }
    return "Ask XWAI anything...";
  }

  get showTypingIndicator(): boolean {
    // Hide the dots once a streaming assistant message is actively rendering.
    const last = this.messages[this.messages.length - 1];
    return this.answer_loading && !(last && last.streaming);
  }

  get showEmptyHero(): boolean {
    return (
      !this.is_viewing_saved_conversation &&
      this.messages.length === 0 &&
      !this.answer_loading
    );
  }

  get chatTitle(): string {
    if (this.is_viewing_saved_conversation && this.current_saved_key) {
      return this.current_saved_key;
    }
    return "New conversation";
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.things.changes.subscribe((t) => {
      this.scroll_to_last();
    });
  }

  resolve_image_request_link(msg: any[]) {
    const imageLinks = msg.find((item) => item[0] === "image_link");
    try {
      if (imageLinks && imageLinks[1].length > 0) {
        return true;
      } else {
        return false;
      }
    } catch {
      return false;
    }
  }

  prompt_agent() {
    const question = this.question.trim();
    if (!question || this.isInputDisabled) {
      return;
    }
    this.messages.push({ role: "user", content: question, timestamp: Date.now() });
    this.question = "";
    this.resetTextareaHeight();
    this.updateCurrentConversationCache();
    this.persistLiveConversation();
    this.requestAnswer(question);
  }

  /**
   * Sends the question to the streaming endpoint and renders the answer
   * progressively: tool-progress notes, native structured visualisations
   * (slice / shot / rag / chart) and token-by-token assistant text.
   *
   * `question` is what the user sees; the actual prompt sent to the backend is
   * prefixed with the current page context (if any) so "this job" resolves.
   */
  // The in-flight streaming message + subscription, held on the instance so the
  // Stop button can finalize/cancel them.
  private streamingMsg: ChatMsg | null = null;
  private currentStream: Subscription | null = null;

  /** Lazily create the assistant message (on first token OR first step). */
  private ensureStreamMsg(): ChatMsg {
    if (!this.streamingMsg) {
      this.streamingMsg = {
        role: "xwi-gpt",
        content: "",
        timestamp: Date.now(),
        streaming: true,
        steps: [],
      };
      this.messages.push(this.streamingMsg);
    }
    return this.streamingMsg;
  }

  private requestAnswer(question: string) {
    this.answer_loading = true;
    this.streaming_status = "";
    this.streamingMsg = null;

    const preamble = this.copilotContext.buildPreamble();
    const sentQuestion = preamble ? preamble + question : question;

    if (!this.useStreaming) {
      this.requestAnswerDirect(sentQuestion);
      return;
    }

    this.currentStream = this.projectService
      .prompt_xwi_gpt_stream(sentQuestion, this.thread_id)
      .subscribe({
        next: (event: { type: string; data?: any }) => {
          switch (event.type) {
            case "update":
              this.streaming_status = event.data;
              break;

            case "step": {
              // record which tool the agent ran (for "how I got this")
              const msg = this.ensureStreamMsg();
              (msg.steps ||= []).push({
                tool: event.data?.tool,
                input: event.data?.input,
              });
              break;
            }

            case "token": {
              this.streaming_status = "";
              const msg = this.ensureStreamMsg();
              msg.content += event.data;
              this.maybeAutoScroll();
              break;
            }

            case "structured_response":
              this.streaming_status = "";
              this.handleStructured(event.data);
              break;

            case "error":
              this.notificationService.error(
                "Error",
                event.data || "There was an unexpected error"
              );
              break;
          }
        },
        error: (err) => {
          this.notificationService.error(
            "Error",
            err?.error?.error || err?.message || "There was an unexpected error"
          );
          this.finishStreaming();
        },
        complete: () => {
          this.finishStreaming();
        },
      });
  }

  /**
   * Non-streaming answer: a single POST that returns the whole answer at once.
   * Robust against proxies that buffer SSE. `sentQuestion` already includes the
   * page-context preamble. Backend errors come back as a 500 with `{error}`.
   */
  private requestAnswerDirect(sentQuestion: string) {
    this.currentStream = this.projectService
      .prompt_xwi_gpt(sentQuestion, this.thread_id)
      .subscribe({
        next: (res: { data?: string; structured_response?: any }) => {
          if (res?.data) {
            const msg = this.ensureStreamMsg();
            msg.content = res.data;
            this.maybeAutoScroll();
          }
          if (res?.structured_response) {
            this.handleStructured(res.structured_response);
          }
        },
        error: (err) => {
          this.notificationService.error(
            "Error",
            err?.error?.error || err?.message || "There was an unexpected error",
          );
          this.finishStreaming();
        },
        complete: () => {
          this.finishStreaming();
        },
      });
  }

  /** Cancel an in-flight answer (aborts the fetch via the Observable teardown). */
  stopStreaming() {
    if (!this.answer_loading) return;
    this.currentStream?.unsubscribe(); // teardown -> AbortController.abort()
    this.currentStream = null;
    if (this.streamingMsg && !this.streamingMsg.content) {
      // nothing streamed yet — drop the empty bubble
      this.messages = this.messages.filter((m) => m !== this.streamingMsg);
      this.streamingMsg = null;
    }
    this.finishStreaming();
  }

  private finishStreaming() {
    const producedAnswer = !!this.streamingMsg?.content;
    if (this.streamingMsg) {
      this.streamingMsg.streaming = false;
      this.parseAndLoadGoogleDriveImages(this.streamingMsg);
    }
    this.streamingMsg = null;
    if (producedAnswer) {
      this.imageFlipperService.notify_answer_complete();
    }
    this.currentStream = null;
    this.streaming_status = "";
    this.answer_loading = false;
    this.updateCurrentConversationCache();
    this.persistLiveConversation();
  }

  /** Friendly label for a tool name shown in the "how I got this" panel. */
  stepLabel(step: AgentStep): string {
    const t = step?.tool || "step";
    if (t === "sql_db_query") return "SQL query";
    if (t === "FindJobsByFunctional") return "Search jobs by functional";
    if (t === "AnalyseConvergence" || t === "MakeConvergenceChart") return "Convergence analysis";
    if (t === "AnalyseCosts" || t === "MakeCostChart") return "Cost analysis";
    if (t === "ProjectHealthSummary") return "Project health";
    if (t === "GetDifferenceInRows") return "Parameter diff";
    return t;
  }

  /** The most useful single detail to show for a step (e.g. the SQL text). */
  stepDetail(step: AgentStep): string {
    const inp = step?.input;
    if (!inp) return "";
    if (typeof inp === "string") return inp;
    if (inp.query) return inp.query;           // sql_db_query
    try {
      return JSON.stringify(inp);
    } catch {
      return "";
    }
  }

  /** Turns a structured tool payload into the right rich message. */
  private handleStructured(structuredData: any) {
    if (!structuredData || !structuredData.content) return;

    if (structuredData.content === "slice") {
      const newMessage: ChatMsg = {
        role: structuredData.is_gif ? "gif-image" : "slice-image",
        content: structuredData,
        timestamp: Date.now(),
      };
      this.messages.push(newMessage);
      if (structuredData.is_gif) {
        this.fetchAndDisplayImages(newMessage);
      }
    } else if (structuredData.content === "shot") {
      this.messages.push({
        role: "shot-image",
        content: structuredData,
        timestamp: Date.now(),
      });
    } else if (structuredData.content === "rag") {
      this.messages.push({
        role: "rag",
        content: structuredData,
        timestamp: Date.now(),
      });
    } else if (structuredData.content === "chart") {
      this.messages.push({
        role: "chart",
        content: structuredData,
        timestamp: Date.now(),
      });
    } else if (structuredData.content === "links") {
      this.messages.push({
        role: "links",
        content: structuredData,
        timestamp: Date.now(),
      });
    }
  }

  /**
   * Re-sends the most recent user question, replacing everything after it.
   */
  regenerateLast() {
    if (this.answer_loading || this.is_viewing_saved_conversation) {
      return;
    }
    let idx = this.messages.length - 1;
    while (idx >= 0 && this.messages[idx].role !== "user") {
      idx--;
    }
    if (idx < 0) {
      return;
    }
    const question = this.messages[idx].content;
    this.messages = this.messages.slice(0, idx + 1);
    this.updateCurrentConversationCache();
    this.persistLiveConversation();
    this.requestAnswer(question);
  }

  isLastMessage(index: number): boolean {
    return index === this.messages.length - 1;
  }

  isCopyable(msg: ChatMsg): boolean {
    return typeof msg.content === "string";
  }

  copyMessage(msg: ChatMsg, index: number) {
    if (typeof msg.content !== "string") {
      return;
    }
    navigator.clipboard.writeText(msg.content).then(() => {
      this.copied_index = index;
      setTimeout(() => {
        if (this.copied_index === index) {
          this.copied_index = null;
        }
      }, 1500);
    });
  }

  editAndResend(msg: ChatMsg) {
    if (typeof msg.content !== "string" || this.isInputDisabled) {
      return;
    }
    this.question = msg.content;
    setTimeout(() => {
      if (this.messageInput?.nativeElement) {
        const textarea = this.messageInput.nativeElement;
        textarea.focus();
        textarea.style.height = "auto";
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
      }
    });
  }

  usePrompt(prompt: string) {
    if (this.isInputDisabled) {
      return;
    }
    this.question = prompt;
    setTimeout(() => this.messageInput?.nativeElement?.focus());
  }

  backToLiveChat() {
    this.imageFlipperService.update_saved_convo_key(null);
  }

  /**
   * Delegated click handler for content rendered via innerHTML
   * (code-block copy buttons, and the "maximise table" button).
   */
  onMessagesClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    // --- maximise a table into the wide overlay ---
    const expandBtn = target.closest(".mk-table-expand") as HTMLElement | null;
    if (expandBtn) {
      const container = expandBtn
        .closest(".mk-table-wrap")
        ?.querySelector(".mk-table-container") as HTMLElement | null;
      if (container) {
        this.expandedTableHtml = this.sanitizer.bypassSecurityTrustHtml(
          container.outerHTML
        );
        const table = container.querySelector("table");
        this.expandedTableCsv = table ? this.tableToCsv(table) : "";
      }
      return;
    }

    // --- copy a code block ---
    const copyBtn = target.closest(".code-copy") as HTMLElement | null;
    if (!copyBtn) {
      return;
    }
    const block = copyBtn.closest(".code-block");
    const pre = block?.querySelector("pre");
    if (!pre) {
      return;
    }
    navigator.clipboard.writeText(pre.textContent || "").then(() => {
      const original = copyBtn.textContent;
      copyBtn.textContent = "Copied!";
      setTimeout(() => {
        copyBtn.textContent = original;
      }, 1500);
    });
  }

  /** Close the maximised-table overlay. */
  closeExpandedTable() {
    this.expandedTableHtml = null;
    this.expandedTableCsv = "";
  }

  /** Serialise a rendered <table> to CSV. */
  private tableToCsv(table: HTMLTableElement): string {
    const esc = (s: string) => {
      const v = (s ?? "").replace(/\s+/g, " ").trim();
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    };
    const rows = Array.from(table.querySelectorAll("tr"));
    return rows
      .map((tr) =>
        Array.from(tr.querySelectorAll("th,td"))
          .map((cell) => esc((cell as HTMLElement).innerText))
          .join(",")
      )
      .join("\n");
  }

  copyExpandedCsv() {
    if (!this.expandedTableCsv) return;
    navigator.clipboard.writeText(this.expandedTableCsv).then(() => {
      this.notificationService.success("Copied", "Table copied as CSV");
    });
  }

  downloadExpandedCsv() {
    if (!this.expandedTableCsv) return;
    this.downloadFile(this.expandedTableCsv, "xwai_table.csv", "text/csv");
  }

  /**
   * Extracts Google Drive links from a message, converts them,
   * and loads them as Base64.
   */
  private parseAndLoadGoogleDriveImages(message: ChatMsg, force: boolean = false) {
    if (typeof message.content !== 'string') return;
    // If we've already processed this message, don't do it again unless forced
    if (message.drive_images && !force) return;

    // just append to drive_images array
    const driveLinks = this.extractAndConvertDriveLinks(message.content);
    if (driveLinks.length > 0) {
      message.drive_images = driveLinks;
    }
  }

  /**
   * Finds GDrive links in text and converts them to direct view links.
   */
  private extractAndConvertDriveLinks(content: string): string[] {
    if (typeof content !== 'string') {
      return [];
    }
    // Regex to find links and capture the File ID
    const regex = /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/view\?usp=drivesdk/g;
    const links = new Set<string>(); // Use a Set to avoid duplicates
    let match;

    while ((match = regex.exec(content)) !== null) {
      const fileId = match[1];
      links.add(`https://lh3.googleusercontent.com/d/${fileId}`);
    }

    return Array.from(links);
  }

  /**
   * The user tweaked the parameters of a shot visualization and hit "Update".
   * `msg.content` is the same object the visualizer holds as `messageContent`,
   * so its `request_body` is already up to date — we just persist the change.
   */
  onShotContentChange(msg: ChatMsg, index: number, requestBody: any) {
    if (!msg || typeof msg.content !== "object" || index == null) return;
    // request_body is stored as an array of JSON strings (current shape) — one
    // entry per image — or a bare value (legacy). Write back in the same shape,
    // stringifying so the array stays homogeneous with how it arrives.
    if (Array.isArray(msg.content.request_body)) {
      msg.content.request_body[index] = JSON.stringify(requestBody);
    } else {
      msg.content.request_body = requestBody;
    }
    this.persistVisualizationChange();
  }

  /**
   * The user tweaked the parameters of a slice visualization and hit "Update".
   * The visualizer emits the rebuilt image_link (params re-encoded in the query
   * string); write it back into the message's image_links so it survives reload.
   */
  onSliceContentChange(msg: ChatMsg, index: number, imageLink: string) {
    if (
      !msg ||
      typeof msg.content !== "object" ||
      !Array.isArray(msg.content.image_links) ||
      index == null
    ) {
      return;
    }
    msg.content.image_links[index] = imageLink;
    this.persistVisualizationChange();
  }

  /**
   * Persists in-place edits to a visualization's request body. When viewing a
   * saved conversation the change is PATCHed to the backend (same endpoint as the
   * title rename) so it is up to date for everyone; otherwise it is written to the
   * live conversation's localStorage copy.
   */
  private persistVisualizationChange() {
    if (this.is_viewing_saved_conversation && this.current_saved_key) {
      const key = this.current_saved_key;
      // keep the in-memory cache for this saved conversation in sync
      this.saved_convos[key] = [...this.messages];
      this.projectService
        .updateConversationMessages(key, this.sanitizeMessagesForStorage(this.messages))
        .pipe(take(1))
        .subscribe({
          error: (err) => {
            console.error("Failed to persist visualization change:", err);
            this.notificationService.error(
              "Error",
              err.error?.error || "Failed to save your changes to the conversation"
            );
          },
        });
    } else {
      this.updateCurrentConversationCache();
      this.persistLiveConversation();
    }
  }

  /** Strip heavy/transient fields from every message before sending/storing. */
  private sanitizeMessagesForStorage(messages: ChatMsg[]): ChatMsg[] {
    return messages.map((msg) => this.sanitizeForStorage(msg));
  }

  private updateCurrentConversationCache() {
    // only cache if we're not viewing a saved conversation
    if (!this.is_viewing_saved_conversation) {
      this.current_conversation_cache = [...this.messages];
    }
  }

  /**
   * Persists the live conversation to localStorage so it survives reloads.
   * Heavy/transient fields (loaded base64 frames, interval ids) are stripped
   * and re-hydrated on restore.
   */
  private persistLiveConversation() {
    if (this.is_viewing_saved_conversation) {
      return;
    }
    try {
      const sanitized = this.messages.map((msg) => this.sanitizeForStorage(msg));
      localStorage.setItem(LIVE_CONVO_STORAGE_KEY, JSON.stringify(sanitized));
    } catch (e) {
      // storage full or unavailable — persistence is best-effort
    }
  }

  private sanitizeForStorage(msg: ChatMsg): ChatMsg {
    const clone: ChatMsg = JSON.parse(JSON.stringify(msg));
    if (clone.content && typeof clone.content === "object") {
      delete clone.content.local_image_urls;
      delete clone.content.flipper_interval_id;
      delete clone.content.images_loading;
      delete clone.content.current_image_index;
      delete clone.content.error;
    }
    delete clone.images_loading;
    return clone;
  }

  private restoreLiveConversation() {
    try {
      const raw = localStorage.getItem(LIVE_CONVO_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const msgs = JSON.parse(raw);
      if (Array.isArray(msgs) && msgs.length > 0) {
        this.messages = msgs;
        this.current_conversation_cache = [...msgs];
        this.processMessagesForImages(this.messages);
      }
    } catch (e) {
      // corrupt entry — drop it
      localStorage.removeItem(LIVE_CONVO_STORAGE_KEY);
    }
  }

  private clearPersistedConversation() {
    try {
      localStorage.removeItem(LIVE_CONVO_STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  }

  scroll_to_last() {
    const chatMessages = document.getElementsByClassName("chat-msg");
    if (chatMessages.length > 0) {
      const lastMessage = chatMessages[chatMessages.length - 1];
      lastMessage.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
  }

  onChatScroll() {
    const el = this.chatScroll?.nativeElement;
    if (!el) {
      return;
    }
    this.show_scroll_fab = el.scrollHeight - el.scrollTop - el.clientHeight > 300;
  }

  /**
   * Keeps the view pinned to the bottom while the answer streams in, but only
   * if the user is already near the bottom — so we don't yank them up if they
   * scrolled back to read something. rAF-batched to stay smooth.
   */
  private _autoScrollQueued = false;
  private maybeAutoScroll() {
    if (this._autoScrollQueued) return;
    const el = this.chatScroll?.nativeElement;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 220;
    if (!nearBottom) return;
    this._autoScrollQueued = true;
    requestAnimationFrame(() => {
      this._autoScrollQueued = false;
      const e = this.chatScroll?.nativeElement;
      if (e) e.scrollTop = e.scrollHeight;
    });
  }

  openGoogleDriveImage(imageUrl: string) {
    window.open(imageUrl, '_blank');
  }

  autoResizeTextarea(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    // Set the height to match content, with a max of ~4 lines - adjustable
    const maxHeight = 120; // 4 lines at ~30px per line
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = newHeight + 'px';
  }

  handleEnterKey(event: KeyboardEvent) {
    // if Shift+Enter, allow new line
    if (event.shiftKey) {
      return;
    }

    // otherwise, prevent default and submit
    event.preventDefault();
    if (!this.isInputDisabled && this.question.trim()) {
      this.prompt_agent();
    }
  }

  resetTextareaHeight() {
    if (this.messageInput && this.messageInput.nativeElement) {
      this.messageInput.nativeElement.style.height = 'auto';
    }
  }

  async startNewChat(notify: boolean = true) {
    try {
      this.answer_loading = true;
      const res = await this.projectService.get_xwi_gpt_thread_id().toPromise();
      this.thread_id = res["data"];

      // clear current conversation cache, persisted copy, and messages
      this.current_conversation_cache = [];
      this.clearPersistedConversation();
      this.resetMessages();

      if (notify) {
        this.notificationService.success("Success", "Started a new conversation");
      }
      this.answer_loading = false;
    } catch (err) {
      console.error("Failed to start a new conversation:", err);
      this.answer_loading = false;
      this.notificationService.error(
        "Error",
        "Failed to start a new conversation. Please try again."
      );
    }
  }

  // Memoized markdown render. The template binds [innerHTML]="getHtml(content)",
  // which Angular re-evaluates on EVERY change-detection cycle. Without this
  // cache the full markdown parse re-ran constantly — making streaming janky and
  // rebuilding the table/Expand-button DOM so fast that clicks were dropped.
  // Returning the same string instance for unchanged content lets Angular skip
  // the innerHTML update entirely (stable, clickable DOM).
  private _htmlCache = new Map<string, string>();

  getHtml(string) {
    if (typeof string !== "string") {
      return "";
    }
    const cached = this._htmlCache.get(string);
    if (cached !== undefined) {
      return cached;
    }
    // Convert Markdown to HTML
    const rawHtml = marked(string, { renderer: renderer });
    // Wrap tables
    const tableWrappedHtml = this.wrapTablesInDiv(rawHtml as string);
    // Collapse long link lists
    const collapsedHtml = this.collapseLongLinkLists(tableWrappedHtml);
    // Add copy buttons to code blocks
    const html = this.wrapCodeBlocks(collapsedHtml);
    // Bound cache so it can't grow without limit over a long session.
    if (this._htmlCache.size > 300) {
      this._htmlCache.clear();
    }
    this._htmlCache.set(string, html);
    return html;
  }

  /**
 * Finds <ul> elements in an HTML string and wraps long lists
 * (mostly links) in a <details> tag.
 */
private collapseLongLinkLists(htmlString: string): string {
  // Regex to find <li> elements that contain a <ul>
  // (group 1) 'beforeUL': Content before the <ul> (e.g., "Various Vp Model Images:")
  // (group 2) 'ulBlock': The entire <ul>...</ul> block
  // (group 3) 'listItems': The content inside the <ul> (all the <li>...</li>)
  // (group 4) 'afterUL': Content after the </ul> (usually empty)
  const nestedListRegex = /<li>(.*?)(<ul>(.*?)<\/ul>)(.*?)<\/li>/gs;

  return htmlString.replace(nestedListRegex, (match, beforeUL, ulBlock, listItems, afterUL) => {


    if (!listItems) listItems = '';

    // Count <li> elements only within this nested list
    const liCount = (listItems.match(/<li/g) || []).length;

    // Count <li> elements that contain a link
    const linkCount = (listItems.match(/<li.*?<a/g) || []).length;

    // --- thresholds ---
    const minItemsToCollapse = 5;
    const minLinkPercentage = 1;

    // Check if this *nested list* is a "long link list"
    const isLongLinkList =
      liCount >= minItemsToCollapse &&
      (liCount === 0 ? false : (linkCount / liCount) >= minLinkPercentage);

    if (isLongLinkList) {

      const summaryText = `View ${liCount} related image links`;

      // We keep the parent <li> and its text (beforeUL),
      // then insert the <details> tag,
      // which wraps the original <ul> block (ulBlock).
      return `
        <li>${beforeUL}
          <details class="link-collapse">
            <summary>${summaryText}</summary>
            ${ulBlock}
          </details>
          ${afterUL}
        </li>
      `;
    } else {

      return match;
    }
  });
}

  wrapTablesInDiv(htmlString: string) {
    const tableRegex = /<table[^>]*>.*?<\/table>/gs; // 'g' for global, 's' for dotall
    return htmlString.replace(tableRegex, (match) => {
      // .mk-table-wrap holds a "maximise" control (handled in onMessagesClick)
      // plus the scrollable .mk-table-container. NOTE: this is a <span>, not a
      // <button> — Angular's innerHTML sanitizer strips <button> elements, so a
      // button would silently disappear. The span is styled to look like one.
      return (
        `<div class="mk-table-wrap">` +
        `<span class="mk-table-expand" title="Expand table">⤢ Expand</span>` +
        `<div class="mk-table-container">${match}</div>` +
        `</div>`
      );
    });
  }

  /**
   * Wraps <pre> blocks so a copy button can float over them.
   * Clicks are handled by the delegated onMessagesClick handler.
   */
  private wrapCodeBlocks(htmlString: string): string {
    const preRegex = /<pre>[\s\S]*?<\/pre>/g;
    return htmlString.replace(preRegex, (match) => {
      // <span>, not <button> — Angular's innerHTML sanitizer strips <button>.
      return `<div class="code-block"><span class="code-copy">Copy</span>${match}</div>`;
    });
  }

  downloadChatObject() {
    const filename = "message_history.json";
    const jsonStr = JSON.stringify(this.messages, null, 2); // null, 2 for pretty-printing

    this.downloadFile(jsonStr, filename, "application/json");
  }

  exportMarkdown() {
    const title = this.is_viewing_saved_conversation && this.current_saved_key
      ? this.current_saved_key
      : "XWAI conversation";
    const lines: string[] = [`# ${title}`, ""];

    for (const msg of this.messages) {
      const who = msg.role === "user" ? "You" : "XWAI";
      const when = msg.timestamp
        ? ` — ${new Date(msg.timestamp).toLocaleString()}`
        : "";
      lines.push(`**${who}${when}:**`, "");
      if (typeof msg.content === "string") {
        lines.push(msg.content, "");
      } else {
        lines.push(`_[interactive ${msg.role} visualization]_`, "");
      }
    }

    this.downloadFile(lines.join("\n"), "xwai_conversation.md", "text/markdown");
  }

  private downloadFile(content: string, filename: string, type: string) {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a); // Clean up the element
    URL.revokeObjectURL(url);
  }

  openSaveConversationMenu() {
    import("./save-conversation-modal/save-conversation-modal.component").then(
      (m) => {
        const modal = this.modalService.create({
          nzTitle: "Save Conversation",
          nzContent: m.SaveConversationModalComponent,
          nzFooter: null,
          nzWidth: 500,
        });

        modal.afterClose.subscribe((conversationName: string) => {
          if (conversationName) {
            this.saveConversation(conversationName);
          }
        });
      }
    );
  }

  private saveConversation(conversationName: string) {
    this.projectService
      .saveConversation(conversationName, this.messages)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.notificationService.success(
            "Success",
            `Conversation "${conversationName}" has been saved successfully!`
          );
          //refresh of conversations list in sidebar
          this.imageFlipperService.trigger_conversations_refresh();
        },
        error: (err) => {
          this.notificationService.error(
            "Error",
            err.error?.error || "Failed to save conversation"
          );
        },
      });
  }

  private fetchAndDisplayImages(message: ChatMsg) {
    if (typeof message.content === "string" || !message.content.image_links)
      return;

    message.content.images_loading = true;

    // Prepend API URL to relative image links
    const fullImageLinks = message.content.image_links.map((url) => {
      if (url.startsWith("/")) {
        return `${AppConfig.settings.apiUrl}${url}`;
      }
      return url;
    });

    const imageObservables = fullImageLinks.map((url) =>
      this.imageLoader.getImageBase64(url)
    );

    forkJoin(imageObservables).subscribe({
      next: (base64Urls) => {
        // The result is an array of Base64 strings
        message.content.local_image_urls = base64Urls;
        message.content.images_loading = false;

        // Start the flipper only AFTER all images have been successfully loaded
        if (message.content.is_gif) {
          this.generateGifFromSlices(message);
        }
      },
      error: (err) => {
        console.error("Failed to load images:", err);
        message.content.images_loading = false;
        message.content.error = "Could not load one or more images.";
        this.notificationService.error(
          "Error",
          "Failed to load images due to an authorization issue."
        );
      },
    });
  }

  private generateGifFromSlices(message: ChatMsg) {
    if (
      typeof message.content === "string" ||
      !message.content.image_links ||
      message.content.image_links.length <= 1
    ) {
      return; // Don't start flipper for single images or invalid data
    }

    message.content.current_image_index = 0;

    // Clear any previous interval on this object to be safe
    if (message.content.flipper_interval_id) {
      clearInterval(message.content.flipper_interval_id);
    }

    // Start the interval to cycle through images every 1.5 seconds
    message.content.flipper_interval_id = setInterval(() => {
      const content = message.content as any;
      content.current_image_index =
        (content.current_image_index + 1) % content.image_links.length;
    }, 1500);
  }
}
